import { useCreditStore } from '@/stores/creditStore';
import { useOrgStore } from '@/stores/orgStore';

const PAID_PLAN_KEYS = new Set(['monthly', 'yearly', 'plan_monthly', 'plan_yearly', 'starter']);
const ALPHA_PLAN_KEYS = new Set(['alpha']);

export interface PaywallStatus {
  /** Alpha org that can't generate → show "Start Trial" modal */
  needsTrial: boolean;
  /** Non-alpha free org that can't afford generation → show upgrade modal */
  needsUpgrade: boolean;
  /** Paid org that ran out of credits → show exhausted modal */
  isExhausted: boolean;
  /** Cancelled trial — subscription trialing + cancel_at_period_end */
  isCancelledTrial: boolean;
  /** Exhausted trial — subscription trialing, not cancelled, but credits = 0 */
  isExhaustedTrial: boolean;
  /** Paid active subscription, out of credits */
  isExhaustedPaid: boolean;
  /** True when the org has no paid plan */
  isFreePlan: boolean;
  /** True when the org has a paid plan */
  hasPaidPlan: boolean;
  /** True when the org is on the alpha plan */
  isAlphaPlan: boolean;
  /** Current credit balance */
  balance: number;
  /** Whether balance covers the cheapest generation (1 credit) */
  canGenerate: boolean;
  /** The org's plan key */
  planKey: string;
  /** Whether balance data has loaded */
  isLoaded: boolean;
  /** When the current period/trial ends (ISO string) */
  currentPeriodEnd: string | null;
  /** Subscription status from the enriched balance API */
  subscriptionStatus: string | null;
}

export function usePaywallCheck(): PaywallStatus {
  const creditBalance = useCreditStore((s) => s.balance);
  const orgs = useOrgStore((s) => s.orgs);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);

  const activeOrg = orgs.find((o) => o.id === activeOrgId);
  const planKey = activeOrg?.plan_key ?? 'alpha';
  const hasPaidPlan = PAID_PLAN_KEYS.has(planKey);
  const isAlphaPlan = ALPHA_PLAN_KEYS.has(planKey);
  const isFreePlan = !hasPaidPlan;

  const balanceCredits = creditBalance?.balanceCredits ?? 0;
  const canGenerate = creditBalance?.canGenerate ?? true;
  const isLoaded = creditBalance !== null;

  // Subscription context from enriched balance API
  const subscriptionStatus = creditBalance?.subscriptionStatus ?? null;
  const cancelAtPeriodEnd = creditBalance?.cancelAtPeriodEnd ?? false;
  const currentPeriodEnd = creditBalance?.currentPeriodEnd ?? null;

  // Alpha user who can't generate → needs to start a free trial
  const needsTrial = isAlphaPlan && !canGenerate && isLoaded;
  // Non-alpha free user who can't generate → generic upgrade
  const needsUpgrade = isFreePlan && !isAlphaPlan && !canGenerate && isLoaded;
  // Paid user out of credits (generic)
  const isExhausted = hasPaidPlan && !canGenerate && isLoaded;

  // More specific states (subsets of isExhausted)
  const isCancelledTrial = hasPaidPlan && cancelAtPeriodEnd && subscriptionStatus === 'trialing' && !canGenerate && isLoaded;
  const isExhaustedTrial = hasPaidPlan && subscriptionStatus === 'trialing' && !cancelAtPeriodEnd && !canGenerate && isLoaded;
  const isExhaustedPaid = hasPaidPlan && subscriptionStatus === 'active' && !canGenerate && isLoaded;

  return {
    needsTrial,
    needsUpgrade,
    isExhausted,
    isCancelledTrial,
    isExhaustedTrial,
    isExhaustedPaid,
    isFreePlan,
    hasPaidPlan,
    isAlphaPlan,
    balance: balanceCredits,
    canGenerate,
    planKey,
    isLoaded,
    currentPeriodEnd,
    subscriptionStatus,
  };
}
